import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatCard } from '@/components/ui/stat-card';
import { SkillCoverageChart } from '@/components/team/skill-coverage-chart';
import { SkillGapsList } from '@/components/team/skill-gaps-list';
import { TopSkillsTable } from '@/components/team/top-skills-table';
import { useTeamProfile, useRefreshTeamProfile } from '@/hooks/use-team';
import { useCurrentUser } from '@/hooks/use-current-user';

export function TeamPage() {
  const { canAccessTeam } = useCurrentUser();
  const { data, isLoading } = useTeamProfile();
  const refresh = useRefreshTeamProfile();

  if (!canAccessTeam) {
    return (
      <div className="py-12 text-center">
        <h2 className="text-lg font-semibold">Access Restricted</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          You don't have permission to view this page. Contact your admin for access.
        </p>
      </div>
    );
  }

  const team = data?.data;
  const totalSkills = team ? Object.keys(team.skillFrequency).length : 0;
  const gapCount = team?.identifiedGaps?.length ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Team Analysis</h1>
        <Button
          size="sm"
          variant="outline"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
        >
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${refresh.isPending ? 'animate-spin' : ''}`} />
          {refresh.isPending ? 'Refreshing...' : 'Refresh Team Profile'}
        </Button>
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">Loading team data...</div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard
              label="Team Members"
              value={team?.memberCount ?? 0}
              subtitle="Profiles in workspace"
              subtitleColor="muted"
            />
            <StatCard
              label="Total Skills"
              value={totalSkills}
              subtitle="Unique skills tracked"
              subtitleColor="blue"
            />
            <StatCard
              label="Skill Gaps"
              value={gapCount}
              subtitle={gapCount > 0 ? 'Skills needing coverage' : 'No gaps found'}
              subtitleColor={gapCount > 0 ? 'yellow' : 'green'}
            />
          </div>

          {/* Charts + Data */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <SkillCoverageChart coverageMap={team?.coverageMap ?? {}} />
            <SkillGapsList gaps={team?.identifiedGaps ?? []} />
          </div>

          <TopSkillsTable
            skillFrequency={team?.skillFrequency ?? {}}
            memberCount={team?.memberCount ?? 0}
          />
        </>
      )}
    </div>
  );
}
